import { p, h, ul, code, tip, info, warn, table, ladder } from './helpers';

const course = {
  id: 'nat-media-scale',
  step: 7,
  title: 'NAT, Media, Scale and WebRTC',
  tagline: 'Production skills: NAT traversal, RTPEngine, load balancing, WebRTC and troubleshooting.',
  icon: 'Rocket',
  colors: ['#00f5d4', '#9b5de5'],
  level: 'Advanced',
  lessons: [
    {
      id: 'nathelper',
      title: 'NAT traversal with nathelper',
      icon: 'Router',
      minutes: 12,
      summary: 'Detect NATed clients, fix their contacts and keep their pinholes open.',
      blocks: [
        code('cfg', `
#!define WITH_NAT

loadmodule "nathelper.so"

modparam("nathelper|registrar", "received_avp", "$avp(RECEIVED)")
modparam("usrloc", "nat_bflag", FLB_NATB)

modparam("nathelper", "natping_interval", 30)     # seconds
modparam("nathelper", "ping_nated_only", 1)
modparam("nathelper", "sipping_bflag", FLB_NATSIPPING)
modparam("nathelper", "sipping_from", "sip:pinger@example.com")`, 'Module setup'),
        h('Detecting NAT'),
        code('cfg', `
route[NATDETECT] {
#!ifdef WITH_NAT
    if (nat_uac_test("19")) {
        if (is_method("REGISTER")) {
            fix_nated_register();      # store the real source in the received AVP
        } else {
            if (is_first_hop()) {
                set_contact_alias();   # encode source ip:port into the Contact
            }
        }
        setflag(FLT_NATS);
    }
#!endif
    return;
}`),
        table(['nat_uac_test bit', 'Test'], [
          ['1', 'Contact header contains a private (RFC 1918 / RFC 6598) address'],
          ['2', 'Source IP differs from the address in the top Via'],
          ['4', 'Top Via contains a private address'],
          ['8', 'SDP contains a private address'],
          ['16', 'Source port differs from the port in the top Via'],
          ['32', 'Source IP itself is private'],
          ['64', 'Request arrived over WebSocket'],
        ]),
        p('`nat_uac_test("19")` is 1 + 2 + 16: any of the three tests marks the request as NATed.'),
        h('Fixing dialogs'),
        ul([
          '`fix_nated_register()` stores the packet source with the registration so later INVITEs to this user are sent to the public address, not the private Contact.',
          '`set_contact_alias()` appends an `;alias=ip~port~proto` parameter to the Contact. Later, `handle_ruri_alias()` turns it back into the destination address for in-dialog requests.',
          'Branch flag `FLB_NATB` is saved with the contact, so Kamailio remembers the callee is NATed when it routes calls to them.',
        ]),
        h('Keepalives'),
        p('With `natping_interval` set, nathelper sends a small UDP packet (or a SIP OPTIONS when the contact carries `FLB_NATSIPPING`) to every NATed contact on that interval, so the router keeps the mapping open and incoming calls arrive.'),
        tip('TCP and TLS are easier', 'Over TCP or TLS the client keeps a connection open, and Kamailio sends requests back over that same connection. Many NAT problems disappear if your phones use TCP or TLS.'),
      ],
    },
    {
      id: 'rtpengine',
      title: 'Media relay with RTPEngine',
      icon: 'AudioLines',
      minutes: 12,
      summary: 'Anchor RTP on a public relay so NATed phones always hear each other.',
      blocks: [
        p('**RTPEngine** (by Sipwise) is a high-performance media proxy with optional in-kernel packet forwarding. Kamailio talks to it over the `ng` control protocol: on every offer and answer, Kamailio sends the SDP to RTPEngine, which allocates ports and returns a rewritten SDP pointing at itself.'),
        ladder('Media anchored through RTPEngine', ['Alice', 'Kamailio', 'RTPEngine', 'Bob'], [
          ['Alice', 'Kamailio', 'INVITE (SDP: 192.168.1.20:10000)', 'req'],
          ['Kamailio', 'RTPEngine', 'offer', 'req'],
          ['RTPEngine', 'Kamailio', 'SDP: 203.0.113.11:30000', 'resp'],
          ['Kamailio', 'Bob', 'INVITE (SDP: 203.0.113.11:30000)', 'req'],
          ['Bob', 'Kamailio', '200 OK (SDP)', 'resp'],
          ['Kamailio', 'RTPEngine', 'answer', 'req'],
          ['RTPEngine', 'Kamailio', 'SDP: 203.0.113.11:30002', 'resp'],
          ['Kamailio', 'Alice', '200 OK (SDP: 203.0.113.11:30002)', 'resp'],
          ['Alice', 'RTPEngine', 'RTP', 'media'],
          ['RTPEngine', 'Bob', 'RTP', 'media'],
        ]),
        code('ini', `
[rtpengine]
interface = 203.0.113.11
listen-ng = 127.0.0.1:2223
port-min = 30000
port-max = 40000
log-level = 6`, '/etc/rtpengine/rtpengine.conf'),
        code('cfg', `
loadmodule "rtpengine.so"
modparam("rtpengine", "rtpengine_sock", "udp:127.0.0.1:2223")

route[NATMANAGE] {
#!ifdef WITH_NAT
    if (is_request()) {
        if (has_totag()) {
            if (check_route_param("nat=yes")) {
                setbflag(FLB_NATB);
            }
        }
    }
    if (!(isflagset(FLT_NATS) || isbflagset(FLB_NATB))) return;

    if (has_body("application/sdp")) {
        rtpengine_manage("replace-origin replace-session-connection");
    }

    if (is_request()) {
        if (!has_totag()) {
            if (t_is_branch_route()) {
                add_rr_param(";nat=yes");   # remember NAT for in-dialog requests
            }
        }
    }
    if (is_reply()) {
        if (isbflagset(FLB_NATB)) {
            if (is_first_hop()) {
                set_contact_alias();
            }
        }
    }
#!endif
    return;
}`, 'kamailio.cfg'),
        ul([
          '`rtpengine_manage()` decides by itself whether to send an offer, an answer or a delete, based on the message type. It is the simplest function to use.',
          'You can call `rtpengine_offer()`, `rtpengine_answer()` and `rtpengine_delete()` explicitly for full control.',
          'Several RTPEngine instances can be listed in `rtpengine_sock` for load sharing and failover.',
        ]),
        warn('Open the firewall', 'Allow UDP on the whole RTP port range (30000-40000 above) to the RTPEngine host, and the ng port only from Kamailio.'),
      ],
    },
    {
      id: 'dispatcher',
      title: 'Load balancing with dispatcher',
      icon: 'Scale',
      minutes: 12,
      summary: 'Spread calls across a pool of servers, probe their health and fail over automatically.',
      blocks: [
        code('text', `
# /etc/kamailio/dispatcher.list
# setid  destination                flags  priority  attributes
1        sip:10.0.0.20:5060         0      0         weight=50;duid=pbx1
1        sip:10.0.0.21:5060         0      0         weight=50;duid=pbx2
2        sip:gw1.carrier.example:5060 0    10
2        sip:gw2.carrier.example:5060 0    5`),
        code('cfg', `
loadmodule "dispatcher.so"

modparam("dispatcher", "list_file", "/etc/kamailio/dispatcher.list")
modparam("dispatcher", "ds_ping_interval", 15)    # OPTIONS probe every 15 s
modparam("dispatcher", "ds_probing_mode", 1)      # probe all destinations
modparam("dispatcher", "ds_probing_threshold", 3) # 3 failures -> inactive
modparam("dispatcher", "ds_inactive_threshold", 3)
modparam("dispatcher", "ds_ping_reply_codes", "class=2;code=403;code=404")

route[DISPATCH] {
    # set 1, algorithm 4 (round robin)
    if (!ds_select_dst("1", "4")) {
        send_reply("503", "No PBX available");
        exit;
    }
    xlog("L_INFO", "dispatching $ci to $du\\n");
    t_on_failure("RTF_DISPATCH");
    route(RELAY);
}

failure_route[RTF_DISPATCH] {
    if (t_is_canceled()) exit;
    # next destination on 5xx or when the destination did not answer at all
    if (t_check_status("5[0-9][0-9]") || (t_branch_timeout() && !t_branch_replied())) {
        ds_mark_dst("ip");                # mark it inactive + probing
        if (ds_next_dst()) {
            t_on_failure("RTF_DISPATCH");
            route(RELAY);
            exit;
        }
    }
}`),
        table(['Algorithm', 'Strategy'], [
          ['0', 'Hash over Call-ID'],
          ['1', 'Hash over From URI'],
          ['2', 'Hash over To URI'],
          ['3', 'Hash over Request-URI'],
          ['4', 'Round-robin'],
          ['5', 'Hash over authentication username'],
          ['6', 'Random'],
          ['7', 'Hash over a pseudo-variable (`hash_pvar` parameter)'],
          ['8', 'Priority order: first active destination wins'],
          ['9', 'Weight-based (`weight` attribute)'],
          ['10', 'Call load: fewest active calls (needs dialog-based tracking)'],
          ['11', 'Relative weight based on load'],
        ]),
        code('bash', `
kamcmd dispatcher.list                       # state of every destination
kamcmd dispatcher.reload                     # re-read the list file
kamcmd dispatcher.set_state ip 1 sip:10.0.0.21:5060   # force inactive for maintenance
kamcmd dispatcher.set_state ap 1 sip:10.0.0.21:5060   # active + probing again`),
        tip('Sticky calls', 'Hashing algorithms send the same key to the same server as long as it is up. Use hash over Call-ID (0) when you want retransmissions and re-INVITEs of a call to land on one PBX even without record-routing.'),
      ],
    },
    {
      id: 'webrtc',
      title: 'WebRTC: SIP over WebSocket',
      icon: 'Globe',
      minutes: 12,
      summary: 'Let browsers register and call with SIP.js or JsSIP, bridged to regular SIP phones.',
      blocks: [
        p('Browsers cannot open raw UDP or TCP sockets, so WebRTC clients send SIP over **secure WebSockets** (RFC 7118). Kamailio accepts the WebSocket handshake through the xhttp module and then processes the SIP messages like any other transport.'),
        code('cfg', `
tcp_accept_no_cl=yes
listen=tls:203.0.113.10:443        # wss:// for browsers
enable_tls=yes

loadmodule "xhttp.so"
loadmodule "websocket.so"
modparam("websocket", "keepalive_mechanism", 1)
modparam("websocket", "keepalive_timeout", 30)

event_route[xhttp:request] {
    set_reply_close();
    set_reply_no_connect();

    if ($Rp != 443) {
        xhttp_reply("403", "Forbidden", "", "");
        exit;
    }

    if ($hdr(Upgrade) =~ "websocket"
            && $hdr(Connection) =~ "Upgrade"
            && $rm =~ "GET") {
        # optional: check $hdr(Origin) against your web domains here
        if (ws_handle_handshake()) {
            exit;
        }
    }
    xhttp_reply("404", "Not Found", "", "");
}`),
        h('Bridging media between WebRTC and SIP'),
        p('WebRTC always uses ICE, DTLS-SRTP and the `UDP/TLS/RTP/SAVPF` profile, while classic phones usually speak plain RTP/AVP. RTPEngine converts between the two:'),
        code('cfg', `
# browser -> SIP phone: remove ICE and DTLS, plain RTP to the phone
if ($proto =~ "ws") {
    rtpengine_manage("RTP/AVP replace-origin replace-session-connection ICE=remove");
}

# SIP phone -> browser: add ICE and DTLS-SRTP for the browser
if ($ru =~ "transport=ws") {
    rtpengine_manage("UDP/TLS/RTP/SAVPF ICE=force rtcp-mux-offer");
}`),
        ul([
          'Browser Contacts use an `.invalid` hostname. Use `nat_uac_test("64")` to detect WebSocket clients and `set_contact_alias()` / `handle_ruri_alias()` so Kamailio can reach them over their existing connection.',
          'Use a real certificate. Browsers reject self-signed certificates for wss:// without any prompt.',
          'Client libraries: **SIP.js** and **JsSIP** are both widely used with Kamailio.',
        ]),
        info('Connection reuse', 'Kamailio can only reach a browser over the WebSocket the browser opened. If that connection drops, calls to that user fail until the browser re-registers.'),
      ],
    },
    {
      id: 'troubleshooting',
      title: 'Monitoring and troubleshooting',
      icon: 'Stethoscope',
      minutes: 12,
      summary: 'Capture traffic, read the right logs, watch statistics and fix the problems everybody hits.',
      blocks: [
        h('Capture first, guess later'),
        code('bash', `
sudo sngrep                                # live ladder diagrams
sudo sngrep -d any port 5060 or port 5061 # filter by port
sudo sngrep -O /tmp/capture.pcap          # save a capture
sudo ngrep -W byline -d any port 5060     # raw text view
sudo tcpdump -i any -s0 -w sip.pcap port 5060   # for Wireshark`),
        h('Statistics worth watching'),
        code('bash', `
kamcmd stats.get_statistics core:          # rcv_requests, fwd_requests, drop_requests ...
kamcmd stats.get_statistics tmx:           # active transactions, 2xx/4xx/5xx counts
kamcmd stats.get_statistics usrloc:        # registered users and contacts
kamcmd stats.get_statistics shmem:         # free and used shared memory
kamcmd dlg.stats_active                    # active calls (dialog module)`),
        tip('Export to Prometheus', 'The `xhttp_prom` module exposes Kamailio statistics in Prometheus format on an HTTP path, so you can graph them in Grafana and alert on drops.'),
        h('Turn up logging without restarting'),
        code('bash', `
kamcmd cfg.seti core debug 3      # DEBUG for everything
kamcmd cfg.seti core debug 2      # back to INFO`),
        p('The `debugger` module goes further: it can log each executed config line for a selected Call-ID or source, so you see exactly which branch of your script a message took.'),
        h('Common problems and fixes'),
        table(['Symptom', 'Usual cause', 'Where to look'], [
          ['404 Not Found for a registered user', 'Domain mismatch (`use_domain`), alias missing, user registered to another server', '`kamctl ul show`, `alias=` lines, R-URI host'],
          ['483 Too Many Hops', 'Routing loop: Kamailio sends the request to itself or between two proxies', 'Missing `alias`, wrong `$du`, sngrep shows the loop'],
          ['One-way or no audio', 'Private IP in SDP, RTP blocked by firewall', 'SDP `c=` lines in sngrep, NATMANAGE, RTP port range'],
          ['Calls drop after ~32 seconds', 'ACK never arrives: the 200 OK Contact or Record-Route is unreachable', 'NAT on the callee side, `advertise`, double Record-Route'],
          ['Registrations expire, incoming calls fail', 'NAT pinhole closed', 'nathelper keepalives, shorter expiry, TCP'],
          ['401 loop, phone never registers', 'Wrong password, wrong realm, or HA1 calculated with another realm', '`auth_check` return code in logs, subscriber table'],
          ['Kamailio will not start', 'Config syntax error or DB version mismatch', '`kamailio -c`, journalctl, the `version` table'],
          ['"no more shm memory"', 'Shared memory too small for the load', 'Increase `-m` (e.g. SHM_MEMORY in /etc/default/kamailio)'],
        ]),
        h('Where to go next'),
        ul([
          'The module documentation on **kamailio.org** for your exact version: every parameter and function is listed there.',
          'The `sr-users` mailing list and the Kamailio community on GitHub discussions.',
          'The **Kamailio World** conference talks, available on YouTube.',
          'Related projects: **RTPEngine**, **Homer/HEP** for capture (the `siptrace` module sends HEP), **Asterisk** and **FreeSWITCH**.',
        ]),
      ],
    },
  ],
  quiz: [
    { q: 'What does nat_uac_test("19") check?', options: ['Only WebSocket', 'Private Contact, Via IP mismatch, or Via port mismatch', 'Only private SDP', 'Whether TLS is used'], answer: 1, explain: '19 = 1 + 2 + 16: private Contact address, source IP differs from Via, source port differs from Via.' },
    { q: 'Why use RTPEngine with Kamailio?', options: ['To authenticate users', 'To relay and convert media, since Kamailio only handles signaling', 'To store registrations', 'To replace the database'], answer: 1, explain: 'Kamailio rewrites SDP and controls RTPEngine, which relays the RTP packets.' },
    { q: 'Which dispatcher algorithm is round-robin?', options: ['0', '4', '8', '10'], answer: 1, explain: 'Algorithm 4 is round-robin. 0 hashes Call-ID, 8 uses priority, 10 uses call load.' },
    { q: 'Which module performs the WebSocket handshake for browsers?', options: ['websocket (with xhttp)', 'rtpengine', 'pike', 'registrar'], answer: 0, explain: 'xhttp receives the HTTP GET and websocket upgrades it with ws_handle_handshake().' },
    { q: 'Calls connect but drop after about 32 seconds. What is the most likely cause?', options: ['Wrong codec', 'The ACK for the 200 OK never reaches the callee', 'The database is down', 'Pike is blocking the caller'], answer: 1, explain: 'Without the ACK the callee retransmits 200 OK for 64 x T1 = 32 s and then gives up and ends the call.' },
  ],
};

export default course;
